<template>
  <div class="k2-sh-body">
    <section class="k2-sec">
      <h4>{{ tx("det.state") }}</h4>
      <div class="k2-steps" role="group">
        <button
          v-for="s in steps"
          :key="s.to"
          type="button"
          :data-fk="'ds-' + s.to"
          :aria-pressed="cur === s.to ? 'true' : 'false'"
          :aria-current="cur === s.to ? 'step' : null"
          @click="$emit('state', s.to)"
        >
          {{ $t(s.label) }}
        </button>
      </div>
      <div class="k2-detinf">
        <span v-if="t.state === 'on-hold'" class="k2c-hold"><k2-icon n="mdi-pause" :s="14" />{{ $t("on-hold") }}</span>
        <span class="k2c-t" :class="'k2c-t--' + inf.cls" :style="{ '--p': pct + '%' }" :aria-label="timerAria">
          <k2-icon v-if="inf.icon" :n="inf.icon" :s="17" />
          <b>{{ timerText }}</b>
        </span>
      </div>
    </section>

    <section class="k2-sec">
      <h4>{{ tx("det.customer") }}</h4>
      <div v-if="t.phone" class="k2-kv">
        <span><k2-icon n="mdi-phone" :s="18" /></span>
        <a class="k2-tel" :href="'tel:' + t.phone"><b>{{ t.phone }}</b></a>
      </div>
      <div v-if="t.type === 0" class="k2-kv">
        <span><k2-icon n="mdi-map-marker-outline" :s="18" /></span>
        <b>{{ t.address || "–" }}</b>
        <small v-if="addressMore">{{ addressMore }}</small>
      </div>
      <div class="k2-kv">
        <span><k2-icon n="mdi-clock-outline" :s="18" /></span>
        <b>{{ tx("card.ordered") }} {{ clock(t.created) }}<template v-if="t.due"> · {{ tx("det.due") }} {{ clock(t.due) }}</template></b>
        <small v-if="t.est && t.acc">{{ tx("det.estimate", { est: dur(t.est), time: clock(t.acc) }) }}</small>
      </div>
      <div v-if="t.restaurant" class="k2-kv">
        <span><k2-icon n="mdi-storefront-outline" :s="18" /></span>
        <b>{{ t.restaurant.name }}</b>
      </div>
    </section>

    <section class="k2-sec">
      <h4>{{ tx("det.items") }}</h4>
      <template v-for="(item, i) in t.items">
        <div :key="'i' + i" class="k2-li">
          <span><b>{{ item.q }}×</b> {{ item.name }}</span>
          <i>{{ money(item.amount) }}</i>
        </div>
        <div v-for="(c, j) in item.choices" :key="'i' + i + 'c' + j" class="k2-li k2-li--ch">
          <span>{{ c.q }}× {{ c.name }}</span>
          <i v-if="c.extra > 0">+{{ money(c.extra) }}</i>
        </div>
      </template>
      <div v-if="t.note" class="k2c-note">
        <k2-icon n="mdi-note-text-outline" :s="18" />
        <span>{{ t.note }}</span>
      </div>
      <div class="k2-li k2-li--tot">
        <span>{{ $t("total") }}</span>
        <i>{{ money(t.total) }}</i>
      </div>
    </section>

    <section class="k2-sec">
      <h4>{{ tx("det.other") }}</h4>
      <div class="k2-btnrow k2-btnrow--w">
        <button type="button" class="k2-btn" data-fk="dx-hold" @click="$emit('status', 'on-hold')">
          <k2-icon n="mdi-pause" :s="18" /><span>{{ tx("menu.hold") }}</span>
        </button>
        <button type="button" class="k2-btn" data-fk="dx-comp" @click="$emit('status', 'completed')">
          <k2-icon n="mdi-check-all" :s="18" /><span>{{ $t("completed") }}</span>
        </button>
        <button type="button" class="k2-btn k2-btn--bad" data-fk="dx-can" @click="$emit('status', 'cancelled')">
          <k2-icon n="mdi-cancel" :s="18" /><span>{{ $t("cancelled") }}</span>
        </button>
        <button type="button" class="k2-btn k2-btn--bad" data-fk="dx-rej" @click="$emit('status', 'rejected')">
          <k2-icon n="mdi-cancel" :s="18" /><span>{{ tx("state.rejected") }}</span>
        </button>
      </div>
    </section>

    <footer class="k2-sh-f">
      <button type="button" class="k2-btn" data-fk="pr" @click="$emit('print')">
        <k2-icon n="mdi-printer" :s="20" /><span>{{ $t("print") }}</span>
      </button>
    </footer>
  </div>
</template>

<script>
import K2Icon from "./K2Icon.vue";
import kds2Text from "@/mixins/kds2Text";
import { fmtClock, ticketInfo } from "@/utils/kdsTickets";

// Detalji narudžbe: stanje (jedan dodir), kupac (telefon, adresa), stavke sa cijenama, ostala stanja, štampa.
export default {
  name: "Kds2Details",
  components: { K2Icon },
  mixins: [kds2Text],
  props: {
    t: { type: Object, required: true },
    now: { type: Number, required: true },
  },
  computed: {
    steps() {
      return [
        { to: "pending", label: "kds_new_orders" },
        { to: "accepted", label: "in-progress" },
        { to: "ready", label: "ready" },
        { to: "delivered", label: "delivered" },
      ];
    },
    // na čekanju se vodi kao Naručeno, in-progress kao U pripremi
    cur() {
      if (this.t.state === "on-hold") return "pending";
      return this.t.state === "in-progress" ? "accepted" : this.t.state;
    },
    inf() {
      return ticketInfo(this.t, this.now);
    },
    pct() {
      return Math.round(this.inf.p);
    },
    timerText() {
      return this.tx("t." + this.inf.key, { d: this.dur(this.inf.d), at: this.inf.at ? fmtClock(this.inf.at) : "" });
    },
    timerAria() {
      return this.tx("a." + this.inf.key, { d: this.dur(this.inf.d), at: this.inf.at ? fmtClock(this.inf.at) : "" });
    },
    addressMore() {
      const t = this.t;
      return [t.city, t.floor ? this.$t("floor") + ": " + t.floor : "", t.apartment ? this.$t("apartment") + ": " + t.apartment : ""].filter(Boolean).join(" • ");
    },
    currency() {
      return this.t.currency || this.restaurantCurrency || "";
    },
  },
  methods: {
    clock(ms) {
      return fmtClock(ms);
    },
    money(value) {
      return (Number(value) || 0).toFixed(2) + " " + this.currency;
    },
  },
};
</script>
